import { useState } from 'react';
import { DateAfter, DateBefore, DateInterval, DateRange, DayOfWeek, Matcher } from 'react-day-picker';
import { View } from '@/shared/ui/core/calendar';
import { DEFAULT_LOCALE } from '@/shared/config';
import { MONTHS, YEARS_PER_PAGE } from '@/shared/constants/calendar';
import { getMaxDate, getMinDate, getYearsRange } from './helpers';


type Params = {
    selected?: Date;
    onSelect?: (date: Date | undefined) => void;
    onClear?: () => void;
    disabled?: boolean | ((date: Date) => boolean) | Date | Date[] | DateRange | DateBefore | DateAfter | DateInterval | DayOfWeek | Matcher[] | undefined
}


export const useCalendar = ({ selected, onSelect, onClear, disabled }: Params) => {
    const [ view, setView ]   = useState<View>('days');
    const [ month, setMonth ] = useState<Date>(selected ?? new Date());

    const handleTitleClick = () => {
        setView((v) => {
            if (v === 'days') return 'months';
            if (v === 'months') return 'years';
            return 'days';
        });
    };

    const handlePrev = () => {
        if (view === 'days') {
            setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1));
        } else if (view === 'months') {
            setMonth(new Date(month.getFullYear() - 1, month.getMonth(), 1));
        } else {
            setMonth(new Date(month.getFullYear() - YEARS_PER_PAGE, month.getMonth(), 1));
        }
    };

    const handleNext = () => {
        if (view === 'days') {
            setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1));
        } else if (view === 'months') {
            setMonth(new Date(month.getFullYear() + 1, month.getMonth(), 1));
        } else {
            setMonth(new Date(month.getFullYear() + YEARS_PER_PAGE, month.getMonth(), 1));
        }
    };

    const handleMonthPick = (monthIndex: number) => {
        setMonth(new Date(month.getFullYear(), monthIndex, 1));
        setView('days');
    };

    const handleClear = () => {
        onSelect?.(undefined);
        onClear?.();
    };

    const handleYearPick = (year: number) => {
        setMonth(new Date(year, month.getMonth(), 1));
        setView('months');
    };

    const getTitle = () => {
        if (view === 'years') {
            const years = getYearsRange(month.getFullYear());
            return `${ years[0] } – ${ years[years.length - 1] }`;
        }
        if (view === 'months') {
            return String(month.getFullYear());
        }
        return `${ MONTHS[DEFAULT_LOCALE].full.nominative[month.getMonth()] } ${ month.getFullYear() }`;
    };

    const maxDate = getMaxDate(disabled);
    const minDate = getMinDate(disabled);

    const isMonthDisabled = (monthIndex: number) => {
        const year = month.getFullYear();
        if (maxDate) {
            if (year > maxDate.getFullYear()) return true;
            if (year === maxDate.getFullYear() && monthIndex > maxDate.getMonth()) return true;
        }
        if (minDate) {
            if (year < minDate.getFullYear()) return true;
            if (year === minDate.getFullYear() && monthIndex < minDate.getMonth()) return true;
        }
        return false;
    };

    const isYearDisabled = (year: number) => {
        if (maxDate && year > maxDate.getFullYear()) return true;
        if (minDate && year < minDate.getFullYear()) return true;
        return false;
    };

    const title = getTitle();

    return {
        handleClear,
        handleMonthPick,
        handleNext,
        handlePrev,
        handleTitleClick,
        handleYearPick,
        isMonthDisabled,
        isYearDisabled,
        month,
        setMonth,
        title,
        view,
    };
};
